import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBusinessDashboard } from "../store/slices/businessOwnerSlice";

// Secure storage key
const SUBSCRIPTION_KEY = "business_subscription";

/**
 * Hook that manages subscription data and returns premium status
 * @returns {object} { isPremium: boolean, fetchSubscriptionData: function }
 */
export const usePremiumStatus = () => {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { dashboard } = useSelector((state) => state.businessOwner);
  
  const [subscriptionType, setSubscriptionType] = useState(null);

  // Secure function to get cached subscription data
  const getCachedSubscription = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(SUBSCRIPTION_KEY);
      if (!cachedData) return null;
      return JSON.parse(cachedData);
    } catch (error) {
      console.error("Error reading cached subscription:", error);
      localStorage.removeItem(SUBSCRIPTION_KEY);
      return null;
    }
  }, []);

  // Secure function to cache subscription data
  const cacheSubscription = useCallback((data) => {
    try {
      localStorage.setItem(SUBSCRIPTION_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Error caching subscription:", error);
    }
  }, []);

  // Function to fetch subscription data
  const fetchSubscriptionData = useCallback(async (force = false) => {
    if (!isAuthenticated || user?.role !== "business") {
      return null;
    }

    // Check cache first unless forced
    if (!force) {
      const cached = getCachedSubscription();
      if (cached) {
        setSubscriptionType(cached.subscription);
        return cached;
      }
    }

    try {
      const result = await dispatch(getBusinessDashboard());
      
      if (result.type === "businessOwner/getBusinessDashboard/fulfilled") {
        const dashboardData = result.payload?.data;
        const businessData = dashboardData?.business;
        
        if (businessData) {
          const subscriptionData = {
            subscription: businessData.subscription,
            businessName: businessData.name,
            businessUuid: businessData.uuid,
            lastUpdated: new Date().toISOString()
          };
          
          cacheSubscription(subscriptionData);
          setSubscriptionType(businessData.subscription);
          
          return subscriptionData;
        }
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    }

    return null;
  }, [dispatch, isAuthenticated, user?.role, getCachedSubscription, cacheSubscription]);

  // Initialize subscription data on mount or when auth state changes
  useEffect(() => {
    if (isAuthenticated && user?.role === "business") {
      const cached = getCachedSubscription();
      if (cached) {
        setSubscriptionType(cached.subscription);
      } else {
        fetchSubscriptionData();
      }
    } else {
      setSubscriptionType(null);
      localStorage.removeItem(SUBSCRIPTION_KEY);
    }
  }, [isAuthenticated, user?.role, getCachedSubscription, fetchSubscriptionData]);

  // Update subscription when dashboard data changes
  useEffect(() => {
    if (dashboard?.business?.subscription) {
      const newSubscription = dashboard.business.subscription;
      if (newSubscription !== subscriptionType) {
        setSubscriptionType(newSubscription);
        
        const subscriptionData = {
          subscription: newSubscription,
          businessName: dashboard.business.name,
          businessUuid: dashboard.business.uuid,
          lastUpdated: new Date().toISOString()
        };
        cacheSubscription(subscriptionData);
      }
    }
  }, [dashboard, subscriptionType, cacheSubscription]);

  // Return premium status and fetch function
  return {
    isPremium: subscriptionType === "Premium",
    fetchSubscriptionData
  };
};

export default usePremiumStatus;